import { Integration } from "/app/src/models";
import Box from "/app/src/components/generic/components/box";
import { useCallback } from "react";
import { Form, Input, Select, SubmitButton } from "formik-antd";
import { useTranslation } from "react-i18next";
import { Formik, FormikHelpers, FormikProps } from "formik";
import HeroDivider from "/app/src/components/HeroUi/Divider";
import GridWrapper from "/app/src/components/generic/gridWrapper";
import useMultipleSettings from "/app/src/hooks/useMultipleSettings";

interface FormValues {
  processingType: string | undefined;
  orderFilters: string | undefined;
}

/**
 * GeneralSettings component renders a form for configuring integration settings.
 *
 * @param {Object} props - Component props.
 * @param {Integration} props.integration - Integration object containing settings.
 * @returns {JSX.Element} The rendered GeneralSettings component.
 */
export default function GeneralSettings({
  integration,
}: {
  integration: Integration;
}) {
  const { t } = useTranslation();

  const { submitHandler, processingType, orderFilters } = useMultipleSettings({
    integrationId: integration.id,
    settingTypes: ["processingType", "orderFilters"],
  });

  /**
   * Handles form submission by calling the submitHandler and resetting the form.
   *
   * @param {FormValues} values - The current form values.
   * @param {FormikHelpers<FormValues>} actions - Formik form helpers.
   * @returns {Promise<void>} A promise that resolves when submission is complete.
   */
  const onSubmit = useCallback(
    async (values: FormValues, actions: FormikHelpers<FormValues>) => {
      await submitHandler(values);
      actions.resetForm();
    },
    [submitHandler],
  );

  /**
   * Renders the settings form UI.
   *
   * @param {FormikProps<FormValues>} props - Formik form properties.
   * @returns {JSX.Element} The settings form component.
   */
  const settingsForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty, isSubmitting }) => (
        <Form layout="vertical">
          <div className="flex justify-between items-center">
            <h2 className="text-[18px]">{t("translation:general_settings")}</h2>
            <SubmitButton type="primary" disabled={!dirty || isSubmitting}>
              {t("translation:save")}
            </SubmitButton>
          </div>
          <HeroDivider className="my-3" />

          <GridWrapper responsive={{ sm: 1, md: 1, lg: 3 }}>
            <Form.Item
              name="processingType"
              label={t("translation:processing_type")}
              hasFeedback={false}
            >
              <Select name="processingType" size="large">
                <Select.Option value="automaticProcessing">
                  {t("translation:auto_processing")}
                </Select.Option>
                <Select.Option value="manualProcessing">
                  {t("translation:manual_processing")}
                </Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="orderFilters"
              label={t("translation:order_filters")}
              hasFeedback={false}
            >
              <Input name="orderFilters" size="large" />
            </Form.Item>
          </GridWrapper>
        </Form>
      ),
      [t],
    );

  return (
    <Box>
      <Formik
        initialValues={{
          processingType: processingType || "automaticProcessing",
          orderFilters: orderFilters || "",
        }}
        onSubmit={onSubmit}
        enableReinitialize
        component={settingsForm}
      />
    </Box>
  );
}
